import { DownloadOutlined } from "@ant-design/icons";
import { Button } from "antd";
import ExcelJS from "exceljs";
// @ts-ignore
import { saveAs } from "file-saver";

type DownloadXLSProps = {
    data: any[];
    fileName: string;
    disabled: boolean;
};

export default function DownloadXLS({ data, fileName, disabled }: DownloadXLSProps) {
    const downloadExcel = async () => {
        if (!data || data.length === 0) return; // Safety: do nothing if no data

        // 1️⃣ Transform data
        const transformedData = data.map((item) => {
            // Combine name fields
            const fullName = [item["Ազգանուն"], item["Անուն"], item["Հայրանուն"]]
                .filter(Boolean)
                .join(" ");

            // Combine Տարածք info: Մարզ + Տարածքային կազմակերպություն
            const territory = [item["Մարզ"], item["Տարածքային կազմակերպություն"]]
                .filter(Boolean)
                .join(", ");

            return {
                Հ: item.id ?? null,
                "Ազգանուն, Անուն, Հայրանուն": fullName || null,
                "ՀԾ Կոդ": item["ՀԾ Կոդ"] || null,
                Տարածք: territory || null,
                Անձնագիր: item["Անձնագիր"] || null,
                ՀԾՀ: item["ՀԾՀ"] || null,
                Հեռախոս: item["Հեռախոսահամար"] || null,
                "Ծնդ Տարեթիվ": item["Ծնդ Տարեթիվ"] ?? null,
                "Դիմում լրացնելու օր": item["Դիմումի ավարտի ամսաթիվը"] ?? null,
                Սեռ: item["Սեռ"] ?? null,
                Կարգավիճակ: item["Կարգավիճակ"] ?? null,
                "Կուսակցական դառնալու օր": item["Անդամ դառնալու ամսաթիվը"] ?? null,
                Նշումներ: item["Նշումներ"] ?? null,
            };
        });

        // 2️⃣ Create workbook and worksheet
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Sheet1");

        // 3️⃣ Prepare headers
        const allKeys = Object.keys(transformedData[0]);
        const notesKey = "Նշումներ";
        const filteredKeys = allKeys.filter((key) => key !== notesKey);
        const headers = [...filteredKeys, notesKey]; // "Նշումներ" always last

        worksheet.addRow(headers);

        worksheet.getRow(1).eachCell((cell: ExcelJS.Cell) => {
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FF4F81BD" }, // blue
            };
            cell.font = {
                color: { argb: "000000" },
                bold: true,
            };
        });

        transformedData.forEach((row) => {
            const values = headers.map((key) => (row as any)[key]);
            const addedRow = worksheet.addRow(values);

            const hasDeleted = values.some((val) => String(val).includes("Հեռացված"));

            if (hasDeleted) {
                const totalColumns = worksheet.columnCount;

                for (let col = 1; col <= totalColumns; col++) {
                    const cell = addedRow.getCell(col);

                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "F79646" }, // orange
                    };

                    cell.font = {
                        color: { argb: "000000" },
                    };
                }
            }
        });

        const notesColumnIndex = headers.indexOf("Նշումներ") + 1;

        if (notesColumnIndex > 0) {
            // worksheet.eachRow((row: ExcelJS.Row, rowNumber: number) => {
            //     if (rowNumber === 1) return; // skip header

            //     const cell = row.getCell(notesColumnIndex);

            //     cell.fill = {
            //         type: "pattern",
            //         pattern: "solid",
            //         fgColor: { argb: "d99594" }, // 🔴 red
            //     };

            //     cell.font = {
            //         color: { argb: "000000" }, // white text
            //     };
            // });

            worksheet.eachRow((row: ExcelJS.Row, rowNumber: number) => {
                if (rowNumber === 1) return;

                const cell = row.getCell(notesColumnIndex);

                // detect if row is already orange (check first cell for example)
                const firstCell = row.getCell(1);
                const isOrangeRow =
                    firstCell.fill &&
                    (firstCell.fill as ExcelJS.FillPattern).fgColor?.argb === "F79646";

                const cellValue = cell.value;
                const isEmpty =
                    cellValue === null ||
                    cellValue === undefined ||
                    String(cellValue).trim() === "";

                // 🟧 Orange only if row is orange AND cell has value
                if (isOrangeRow && !isEmpty) {
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "F79646" }, // orange
                    };
                } else {
                    // 👉 otherwise make it red
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "d99594" }, // red
                    };
                }

                cell.font = {
                    color: { argb: "000000" },
                };
            });
        }
        const minWidth = 10; // optional minimum width
        const maxWidth = 40; // optional maximum width

        headers.forEach((header, colIndex) => {
            let maxLength = 0;

            worksheet.eachRow((row: ExcelJS.Row, rowNumber: number) => {
                if (rowNumber === 1) return; // skip header if you want

                const cellValue = row.getCell(colIndex + 1).value;

                // Only consider non-empty strings
                if (cellValue && String(cellValue).trim().length > 0) {
                    maxLength = Math.max(maxLength, String(cellValue).length);
                }
            });

            const adjustedWidth = Math.min(Math.max(maxLength + 2, minWidth), maxWidth);
            worksheet.getColumn(colIndex + 1).width = adjustedWidth;
        });
        // 5️⃣ Export
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `${fileName}.xlsx`);
    };

    return (
        <Button
            type="default"
            className="download-csv"
            disabled={disabled}
            icon={<DownloadOutlined />}
            onClick={downloadExcel}
        >
            Download Excel
        </Button>
    );
}
